import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import type { TimeLog } from "@/types/db";
import { exportToCsv, fmtHours } from "@/lib/utils";
import { MyPayPeriodCard } from "@/components/MyPayPeriodCard";
import { businessDay, businessTime, businessToday, businessRange } from "@/lib/time-period";

function PhotoThumb({ path }: { path: string | null }) {
  const [url, setUrl] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!path) return;
    let alive = true;
    void supabase.storage.from("kiosk-photos").createSignedUrl(path, 60 * 60).then(({ data }) => {
      if (alive) setUrl(data?.signedUrl ?? null);
    });
    return () => { alive = false; };
  }, [path]);
  if (!path) return <span className="text-gray2 text-xs">—</span>;
  if (!url) return <span className="inline-block h-9 w-9 animate-pulse rounded bg-line" />;
  return (
    <>
      <img src={url} alt="clock-in" className="inline-block h-9 w-9 cursor-pointer rounded object-cover ring-1 ring-line hover:ring-teal" onClick={() => setOpen(true)} />
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4" onClick={() => setOpen(false)}>
          <img src={url} alt="clock-in" className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-soft" />
        </div>
      )}
    </>
  );
}

export function TimeTracking() {
  const { employee } = useAuth();
  const [logs, setLogs] = useState<TimeLog[]>([]);
  const [from, setFrom] = useState(() => businessDay(new Date(Date.now() - 30 * 24 * 3600 * 1000)));
  const [to, setTo] = useState(() => businessToday());
  const [showManual, setShowManual] = useState(false);
  const [mDate, setMDate] = useState("");
  const [mIn, setMIn] = useState("");
  const [mOut, setMOut] = useState("");
  // HQ-TIME-001: in and out shared one date, so an overnight shift could not be
  // expressed at all — entering 22:00 to 06:00 produced minus sixteen hours
  // rather than eight. The end gets its own calendar day.
  const [mOvernight, setMOvernight] = useState(false);
  const [mNotes, setMNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => { void refresh(); }, [from, to, employee?.id]);

  async function refresh() {
    if (!employee) return;
    // HQ-PAYROLL-001: whole BUSINESS days, the same ones payroll will use.
    const { startIso, endIso } = businessRange(from, to);
    const { data, error } = await supabase
      .from("hq_time_logs")
      .select("*")
      .eq("employee_id", employee.id)
      .gte("clock_in_time", startIso)
      .lt("clock_in_time", endIso)
      .order("clock_in_time", { ascending: false });
    if (error) setError(error.message);
    else setLogs(data as TimeLog[]);
  }

  async function submitManual(e: React.FormEvent) {
    e.preventDefault();
    if (!employee) return;
    setBusy(true); setError(null); setInfo(null);
    const inAt = new Date(`${mDate}T${mIn}`);
    // An overnight shift ends on the following calendar day.
    const outAt = mOut
      ? new Date(`${mDate}T${mOut}`)
      : null;
    if (outAt && mOvernight) outAt.setDate(outAt.getDate() + 1);

    // Checked here so the person is told immediately and in their own terms;
    // the database enforces it too (sql/2026-12-28_time_log_chronology.sql),
    // because this is not the only way a row can be written.
    if (outAt && outAt <= inAt) {
      setBusy(false);
      setError(mOvernight
        ? "That still ends before it starts. Check the times."
        : "Clock-out is before clock-in. If this was an overnight shift, tick “ends the next day”.");
      return;
    }

    const inIso = inAt.toISOString();
    const outIso = outAt ? outAt.toISOString() : null;
    const { error } = await supabase.from("hq_time_logs").insert({
      employee_id: employee.id,
      clock_in_time: inIso,
      clock_out_time: outIso,
      notes: mNotes || null,
      manual_entry: true,
      verification_status: "unverified",
    });
    setBusy(false);
    if (error) setError(error.message);
    else {
      setInfo("Manual entry saved.");
      setShowManual(false);
      setMDate(""); setMIn(""); setMOut(""); setMNotes(""); setMOvernight(false);
      await refresh();
    }
  }

  function onExport() {
    exportToCsv(
      `hq-time-${from}-to-${to}.csv`,
      ["Date", "Clock In", "Clock Out", "Hours", "Manual?", "Verification", "Notes"],
      logs.map(l => [
        businessDay(l.clock_in_time),
        businessTime(l.clock_in_time),
        l.clock_out_time ? businessTime(l.clock_out_time) : "",
        l.hours_worked ?? "",
        l.manual_entry ? "yes" : "no",
        l.verification_status,
        l.notes ?? "",
      ]),
    );
  }

  const totalHours = logs.reduce((s, l) => s + (l.hours_worked ?? 0), 0);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Time Tracking</h1>
        <div className="flex gap-2">
          <button onClick={onExport} className="btn btn-ghost">↓ CSV</button>
          <button onClick={() => setShowManual(s => !s)} className="btn">+ Manual Entry</button>
        </div>
      </div>

      {showManual && (
        <div className="card">
          <h2 className="mb-3 text-lg font-semibold">Manual time entry</h2>
          <p className="mb-4 text-xs text-gray2">Use this for missed clock-ins or corrections. Marked as <strong>unverified</strong> for admin review.</p>
          {error && <div className="mb-3 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{error}</div>}
          <form onSubmit={submitManual} className="grid gap-4 sm:grid-cols-2">
            <div><label className="label">Date</label><input type="date" required className="input" value={mDate} onChange={e => setMDate(e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="label">In</label><input type="time" required className="input" value={mIn} onChange={e => setMIn(e.target.value)} /></div>
              <div><label className="label">Out</label><input type="time" className="input" value={mOut} onChange={e => setMOut(e.target.value)} /></div>
            </div>
            <div className="flex items-center gap-2">
              <input id="m-overnight" type="checkbox" checked={mOvernight} onChange={e => setMOvernight(e.target.checked)} />
              <label htmlFor="m-overnight" className="text-sm text-charcoal">Ends the next day (overnight shift)</label>
            </div>
            <div className="sm:col-span-2"><label className="label">Notes</label><input className="input" value={mNotes} onChange={e => setMNotes(e.target.value)} placeholder="Why was this entry made manually?" /></div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button type="button" className="btn btn-ghost" onClick={() => setShowManual(false)}>Cancel</button>
              <button className="btn" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
            </div>
          </form>
        </div>
      )}

      {info && <div className="rounded-lg bg-ok-bg px-3.5 py-2.5 text-sm text-ok">{info}</div>}

      <MyPayPeriodCard />

      <div className="card">
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div><label className="label">From</label><input type="date" className="input" value={from} onChange={e => setFrom(e.target.value)} /></div>
          <div><label className="label">To</label><input type="date" className="input" value={to} onChange={e => setTo(e.target.value)} /></div>
          <div className="ml-auto"><div className="label">Total in range</div><div className="text-2xl font-bold text-teal-dark">{fmtHours(totalHours)}</div></div>
        </div>

        {logs.length === 0 ? (
          <p className="py-10 text-center text-sm text-gray2">No time logs in this range.</p>
        ) : (
          <>
            {/* Desktop: table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-xs uppercase tracking-wider text-gray2">
                    <th className="py-2">Photo</th><th>Date</th><th>Clock in</th><th>Clock out</th>
                    <th className="text-right">Hours</th><th>Verification</th><th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(l => (
                    <tr key={l.id} className="border-b border-line/70">
                      <td className="py-2"><PhotoThumb path={l.kiosk_photo_path} /></td>
                      <td className="py-2">{businessDay(l.clock_in_time)}</td>
                      <td className="py-2 text-gray2">{businessTime(l.clock_in_time)}</td>
                      <td className="py-2 text-gray2">{l.clock_out_time ? businessTime(l.clock_out_time) : <span className="text-ok">— open —</span>}</td>
                      <td className="py-2 text-right font-semibold">{fmtHours(l.hours_worked ?? null)}</td>
                      <td className="py-2"><span className={`pill ${l.verification_status === "verified" ? "pill-ok" : l.verification_status === "unverified" ? "pill-warn" : "pill-mint"}`}>{l.verification_status}</span></td>
                      <td className="py-2 text-gray2">{l.notes ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: cards */}
            <div className="md:hidden space-y-2">
              {logs.map(l => (
                <div key={l.id} className="rounded-xl border border-line bg-white p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <PhotoThumb path={l.kiosk_photo_path} />
                        <div className="min-w-0">
                          <div className="text-sm font-semibold">{businessDay(l.clock_in_time)}</div>
                          <span className={`pill mt-0.5 inline-block ${l.verification_status === "verified" ? "pill-ok" : l.verification_status === "unverified" ? "pill-warn" : "pill-mint"}`}>
                            {l.verification_status}
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                        <span className="rounded bg-offwhite px-2 py-0.5 text-gray2">In: <span className="text-charcoal font-medium">{businessTime(l.clock_in_time)}</span></span>
                        <span className="rounded bg-offwhite px-2 py-0.5 text-gray2">
                          Out: {l.clock_out_time ? <span className="text-charcoal font-medium">{businessTime(l.clock_out_time)}</span> : <span className="text-ok font-medium">open</span>}
                        </span>
                      </div>
                      {l.notes && <div className="mt-2 text-xs text-gray2">{l.notes}</div>}
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-xl font-bold text-teal-dark">{fmtHours(l.hours_worked ?? null)}</div>
                      <div className="text-[10px] uppercase tracking-wider text-gray2">hrs</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
